// Página pública "Entrar na Rede Everlog" (PO1-3647, épico PO1-3359):
// /public/rede-everlog. A transportadora pede para entrar na Rede (o
// embarcador âncora da Everlog) e declara a cobertura origem → destino, sem
// preço. Mesmo visual (glass-card) e mesmo molde de formulário do aceite de
// convite (ConviteTransportadora): @unform/web + Yup, cliente anônimo.
// Captcha no molde de views/pages/Register. Nada é cadastrado aqui: o pedido
// entra na curadoria da Everlog e sai um protocolo.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, CardHeader, CardBody, CardFooter, Container, Col, Row, Input, FormGroup, Label } from "reactstrap";
import { Link } from "react-router-dom";
import { Form } from "@unform/web";
import * as Yup from "yup";

import gobidLogo from "../../../assets/img/gobid-logo-negativo.png"; // mesmo logo do cabeçalho do gobid.com.br
import fundoCaminhao from "../../../assets/img/rede-fundo-caminhao.jpg"; // gerado no Canva
import getValidationErrors from "../../../utils/getValidationErrors";
import CustomInputGroupText from "components/CustomInputGroupText";
import CoberturaEditor, { validarCobertura, coberturaParaPayload, rotaVazia } from "components/CoberturaEditor";
import ApiCotacaoForm, { API_VAZIA, validarApiCotacao, apiCotacaoParaPayload } from "components/ApiCotacaoForm";
import EscolherArquivos from "components/EscolherArquivos";
import { useToast } from "../../../hooks/toast";
import { isValidCnpj, digitsOnly, formatDocument } from "utils/formatDoc";
import { ACCEPT, MAX_ARQUIVOS, validarAnexos, fmtTamanho } from "utils/anexosTabelaReferencia";
import { obterConfigRede, novoCaptchaRede, listarEstadosRede, buscarCidadesRede, consultarCnpjRede, enviarPedidoRede } from "services/redeEverlogPublicApi";
import "../../pages/Login/login.css";
import "./rede.css";

const textoSuave = { color: "#555" };
const textoCentro = { ...textoSuave, textAlign: "center" };
const tituloSecao = { color: "#333", fontSize: "0.95rem", fontWeight: 600, margin: "1.8rem 0 0.5rem" };
const labelStyle = { color: "#555", fontSize: "0.78rem", marginBottom: 4 };
const erroStyle = { color: "#ec250d", fontSize: "0.82rem", marginTop: "0.4rem" };
const caixaStyle = { background: "#f7f9fb", border: "1px solid #d6dde4", borderRadius: 8, padding: "10px 12px", marginBottom: 12 };

// Campos do 400 que são do formulário unform (os outros são estado da tela).
const CAMPOS_FORM = { cnpj: "cnpj", nomeResponsavel: "nomeResponsavel", email: "email", telefone: "telefone", captcha: "captchaAnswer" };

export default function RedeEverlog() {
  const formRef = useRef(null);
  const { addToast } = useToast();

  const [carregado, setCarregado] = useState(false);
  const [indisponivel, setIndisponivel] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [cnpjInfo, setCnpjInfo] = useState(null); // resposta de /cnpj/:cnpj
  const [consultandoCnpj, setConsultandoCnpj] = useState(false);
  const [razaoSocial, setRazaoSocial] = useState("");
  const [cobertura, setCobertura] = useState([rotaVazia()]);
  const [erroCobertura, setErroCobertura] = useState(null);
  const [arquivos, setArquivos] = useState([]);
  const [erroArquivos, setErroArquivos] = useState(null);
  const [visivel, setVisivel] = useState(false);
  const [api, setApi] = useState(API_VAZIA);
  const [errosApi, setErrosApi] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [erroGeral, setErroGeral] = useState(null); // { code, texto }
  const [protocolo, setProtocolo] = useState(null);
  const [comecou, setComecou] = useState(false); // boas-vindas → formulário

  // O layout público é escuro (wrapper/full-page); só esta página fica clara.
  useEffect(() => {
    document.body.classList.add("rede-everlog-claro");
    return () => document.body.classList.remove("rede-everlog-claro");
  }, []);

  const refreshCaptcha = useCallback(async () => {
    try {
      setCaptcha(await novoCaptchaRede());
    } catch (err) {
      console.error("[RedeEverlog] captcha:", err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await obterConfigRede();
        if (cancelled) return;
        if (!cfg || cfg.ativa === false) setIndisponivel(true);
        else setCaptcha(cfg.captcha || null);
      } catch (err) {
        if (cancelled) return;
        // 404 = Rede desligada (interruptor único); qualquer outra falha
        // também deixa a página indisponível, sem formulário quebrado.
        setIndisponivel(true);
      } finally {
        if (!cancelled) setCarregado(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Consulta a razão social quando o CNPJ sai do campo. Com `disponivel:false`
  // (Receita fora do ar), a transportadora digita a razão social à mão.
  const consultarCnpj = useCallback(async () => {
    const valor = formRef.current ? formRef.current.getFieldValue("cnpj") : "";
    const digitos = digitsOnly(valor);
    if (!isValidCnpj(digitos)) return;
    if (cnpjInfo && cnpjInfo.cnpj === digitos) return;
    setConsultandoCnpj(true);
    try {
      const r = await consultarCnpjRede(digitos);
      setCnpjInfo({ ...(r || {}), cnpj: digitos });
      if (r && r.disponivel) setRazaoSocial(r.razaoSocial || "");
    } catch (err) {
      const data = (err.response && err.response.data) || {};
      if (err.response && err.response.status === 400 && formRef.current) formRef.current.setFieldError("cnpj", data.error || "CNPJ inválido");
      setCnpjInfo({ disponivel: false, cnpj: digitos });
    } finally {
      setConsultandoCnpj(false);
    }
  }, [cnpjInfo]);

  const escolherArquivos = (lista) => {
    const novos = Array.from(lista || []);
    const todos = arquivos.concat(novos).slice(0, MAX_ARQUIVOS + 1);
    setArquivos(todos);
    setErroArquivos(validarAnexos(todos));
  };

  const removerArquivo = (i) => {
    const todos = arquivos.filter((_, idx) => idx !== i);
    setArquivos(todos);
    setErroArquivos(validarAnexos(todos));
  };

  const tratarErro = useCallback(
    (error) => {
      const data = (error.response && error.response.data) || {};
      const status = error.response && error.response.status;
      let texto = data.error || "Não foi possível enviar o pedido.";
      let code = null;
      if (status === 400 && data.field) {
        const campoForm = CAMPOS_FORM[data.field];
        if (campoForm && formRef.current) {
          formRef.current.setFieldError(campoForm, data.field === "captcha" ? "Captcha inválido. Tente de novo." : data.error || "Confira este campo.");
          texto = data.field === "captcha" ? "Captcha inválido. Tente de novo." : data.error || "Confira os campos destacados.";
        } else if (data.field === "cobertura") {
          setErroCobertura(data.error || "Confira a cobertura.");
        } else if (data.field === "tabelas") {
          setErroArquivos(data.error || "Confira os arquivos.");
        } else if (data.field === "api") {
          setErrosApi({ tipo: data.error || "Confira a API." });
        }
      } else if (status === 409 && data.code === "ja_tem_acesso") {
        code = "ja_tem_acesso";
        texto = "Sua transportadora já tem acesso ao portal. Entre pelo login e, no Cadastro, revise a cobertura e os contatos.";
      } else if (status === 409 && data.code === "pedido_em_analise") {
        code = "pedido_em_analise";
        texto = "Já existe um pedido desta transportadora em análise. A Everlog responde pelo e-mail informado; não é preciso enviar de novo.";
      } else if (status === 429) {
        code = "limite";
        texto = "Muitas tentativas. Aguarde alguns minutos e tente de novo.";
      }
      setErroGeral({ code, texto });
      addToast({ place: "tr", type: "danger", title: "Erro ao enviar o pedido", description: texto, icon: "tim-icons icon-alert-circle-exc" });
      // O captcha é de uso único: qualquer erro do servidor pede um novo.
      refreshCaptcha();
    },
    [addToast, refreshCaptcha],
  );

  const handleSubmit = useCallback(
    async (data) => {
      setErroGeral(null);
      try {
        if (formRef.current) formRef.current.setErrors({});
        const schema = Yup.object().shape({
          cnpj: Yup.string()
            .required("CNPJ é obrigatório")
            .test("cnpj", "CNPJ inválido", (v) => isValidCnpj(digitsOnly(v)))
            .test("matriz", "Informe o CNPJ da matriz (final 0001)", (v) => digitsOnly(v).slice(8, 12) === "0001"),
          nomeResponsavel: Yup.string().required("Nome do responsável é obrigatório"),
          email: Yup.string().required("E-mail é obrigatório").email("E-mail inválido"),
          telefone: Yup.string()
            .required("Telefone ou WhatsApp é obrigatório")
            .test("telefone", "Informe o DDD e o número", (v) => digitsOnly(v).length >= 10 && digitsOnly(v).length <= 13),
          captchaAnswer: captcha ? Yup.string().required("Responda o captcha") : Yup.string(),
        });
        await schema.validate(data, { abortEarly: false });

        const eCob = validarCobertura(cobertura);
        const eArq = validarAnexos(arquivos);
        const eApi = validarApiCotacao(api);
        setErroCobertura(eCob);
        setErroArquivos(eArq);
        setErrosApi(eApi);
        if (eCob || eArq || Object.keys(eApi).length) {
          setErroGeral({ code: null, texto: eCob || eArq || "Confira os dados da API de cotação." });
          return;
        }
        if (cnpjInfo && cnpjInfo.disponivel === false && !razaoSocial.trim()) {
          setErroGeral({ code: null, texto: "Informe a razão social (a Receita não respondeu)." });
          return;
        }

        setSubmitting(true);
        const out = await enviarPedidoRede(
          {
            cnpj: data.cnpj,
            razaoSocial: razaoSocial.trim() || undefined,
            nomeResponsavel: data.nomeResponsavel,
            email: data.email,
            telefone: data.telefone,
            cobertura: coberturaParaPayload(cobertura),
            api: api.temApi ? apiCotacaoParaPayload(api) : undefined,
            visivelEmbarcadores: visivel,
            captchaToken: captcha ? captcha.token : undefined,
            captchaAnswer: data.captchaAnswer,
          },
          arquivos,
        );
        setProtocolo(out && out.protocolo);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          if (formRef.current) formRef.current.setErrors(getValidationErrors(error));
          return;
        }
        tratarErro(error);
      } finally {
        setSubmitting(false);
      }
    },
    [captcha, cobertura, arquivos, api, visivel, razaoSocial, cnpjInfo, tratarErro],
  );

  // Cabeçalho e faixa de topo no padrão do gobid.com.br: a transportadora chega
  // pelo site, então a página parece uma página do site (modo claro).
  const topo = (
    <>
      <header className="rede-topo">
        <div className="rede-topo__in">
          <a className="rede-topo__marca" href="https://www.gobid.com.br" target="_blank" rel="noopener noreferrer">
            <img alt="" src={gobidLogo} />
            <span className="rede-topo__nome">
              <span className="rede-topo__go">Go</span>
              <span className="rede-topo__bid">BID</span>
            </span>
            <span className="rede-topo__sub">| REDE EVERLOG DE TRANSPORTADORAS</span>
          </a>
          <nav className="rede-topo__menu">
            <a href="https://www.gobid.com.br" target="_blank" rel="noopener noreferrer">Sobre o GoBID</a>
            <a href="https://www.gobid.com.br/modulos" target="_blank" rel="noopener noreferrer">Módulos</a>
            <a className="rede-topo__cta" href="https://www.everlogbrasil.com.br" target="_blank" rel="noopener noreferrer">Conheça a Everlog</a>
          </nav>
        </div>
      </header>
      <section className="rede-hero" style={{ backgroundImage: `linear-gradient(90deg, rgba(9,35,54,.92) 0%, rgba(12,60,96,.80) 55%, rgba(12,60,96,.45) 100%), url(${fundoCaminhao})` }}>
        <h1>Rede Everlog</h1>
        <p>
          Sua transportadora na frente dos embarcadores que <b>cotam</b>, fazem <b>leilão</b> e <b>compram frete</b> pelo GoBID.
        </p>
      </section>
    </>
  );

  const cartao = (titulo, corpo, rodape) => (
    <div className="content rede-conteudo">
      {topo}
      <Container>
        <Col className="ml-auto mr-auto" lg="6" md="8">
          <Card className="glass-card rede-card">
            <CardHeader>
              {titulo && <h4>{titulo}</h4>}
            </CardHeader>
            <CardBody>{corpo}</CardBody>
            {rodape && <CardFooter>{rodape}</CardFooter>}
          </Card>
        </Col>
      </Container>
    </div>
  );

  if (!carregado) return cartao(null, <p style={{ ...textoCentro, margin: 0 }}>Carregando…</p>);

  if (indisponivel) {
    return cartao(
      "Rede Everlog",
      <p style={textoCentro} role="alert">
        A entrada na Rede Everlog não está disponível no momento. Tente mais tarde ou fale com a Everlog.
      </p>,
      <div style={{ textAlign: "center" }}>
        <Link to="/auth/login" style={{ color: "#0c3c60", fontSize: 13 }}>
          Ir para o login
        </Link>
      </div>,
    );
  }

  if (protocolo) {
    return cartao(
      "Pedido recebido",
      <div style={textoCentro} data-testid="rede-sucesso">
        <p>
          Seu pedido de entrada na Rede Everlog foi registrado com o protocolo <strong style={{ color: "#0c3c60", fontSize: "1.2rem" }}>#{protocolo}</strong>.
        </p>
        <p style={{ marginBottom: 0 }}>A Everlog analisa o pedido e responde pelo e-mail informado. Quando for aprovado, você recebe o convite para criar o acesso ao portal.</p>
      </div>,
      <div style={{ textAlign: "center" }}>
        <Link to="/auth/login" style={{ color: "#0c3c60", fontSize: 13 }}>
          Ir para o login
        </Link>
      </div>,
    );
  }

  // Boas-vindas: só explica a Rede e abre o formulário (não envia nada).
  if (!comecou) {
    return cartao(
      <>
        Bem-vindo à <strong>Rede Everlog</strong>
      </>,
      <div style={textoSuave} data-testid="rede-boas-vindas">
        <p style={{ fontSize: 14, textAlign: "center" }}>
          Embarcadores cotam, fazem leilão e compram frete pelo <strong className="rede-destaque">GoBID</strong>, a plataforma de compra de frete da Everlog. Na Rede, a sua transportadora aparece para esses embarcadores nas rotas em que atende.
        </p>
        <ul style={{ fontSize: 14, paddingLeft: "1.2rem", margin: "1.2rem 0" }}>
          <li style={{ marginBottom: 6 }}><strong className="rede-destaque">Gratuito:</strong> entrar na Rede não tem custo.</li>
          <li style={{ marginBottom: 6 }}><strong className="rede-destaque">Sem compromisso:</strong> você declara onde atende, sem preço.</li>
          <li><strong className="rede-destaque">Sem garantia de frete:</strong> você passa a ser convidado para cotações, leilões e BIDs.</li>
        </ul>
        <div style={tituloSecao}>Como funciona</div>
        <ol style={{ fontSize: 14, paddingLeft: "1.2rem", marginBottom: 0 }}>
          <li style={{ marginBottom: 6 }}>Você preenche o pedido: empresa, contato e rotas que atende.</li>
          <li style={{ marginBottom: 6 }}>A Everlog analisa e responde pelo e-mail informado.</li>
          <li>Aprovado, você recebe o convite para criar o acesso ao portal.</li>
        </ol>
      </div>,
      <>
        <Button block color="primary" size="lg" type="button" onClick={() => setComecou(true)}>
          Quero entrar na Rede
        </Button>
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <Link to="/auth/login" style={{ color: "#0c3c60", fontSize: 13 }}>
            Já tenho acesso ao portal
          </Link>
        </div>
      </>,
    );
  }

  return (
    <div className="content rede-conteudo">
      {topo}
      <Container>
        <Col className="ml-auto mr-auto" lg="8" md="10">
          <Form ref={formRef} onSubmit={handleSubmit}>
            <Card className="glass-card rede-card">
              <CardHeader>
                  <h4>
                  Entrar na <strong>Rede Everlog</strong>
                </h4>
                <p style={{ ...textoCentro, fontSize: 13, marginTop: 10, marginBottom: 0 }}>
                  Declare onde a sua transportadora atende e apareça para os embarcadores que cotam, leiloam e compram frete pela Everlog. Sem preço e sem compromisso: entrar na Rede não garante frete.
                </p>
              </CardHeader>
              <CardBody>
                <div style={tituloSecao}>Transportadora</div>
                <div onBlur={consultarCnpj}>
                  <CustomInputGroupText name="cnpj" placeholder="CNPJ da matriz *" type="text" addonType="prepend" icon="tim-icons icon-badge" />
                </div>
                {consultandoCnpj ? <p style={{ ...textoSuave, fontSize: 12 }}>Consultando a Receita…</p> : null}
                {cnpjInfo && cnpjInfo.disponivel ? (
                  <div style={{ ...caixaStyle, color: "#333", fontSize: 13 }} data-testid="rede-razao">
                    <div>
                      <strong>{cnpjInfo.razaoSocial}</strong>
                      {cnpjInfo.fantasia ? ` · ${cnpjInfo.fantasia}` : ""}
                    </div>
                    <div style={{ ...textoSuave, fontSize: 12 }}>
                      {formatDocument(cnpjInfo.cnpj)}
                      {cnpjInfo.situacao ? ` · ${cnpjInfo.situacao}` : ""}
                      {cnpjInfo.matriz === false ? " · atenção: este CNPJ não é a matriz" : ""}
                    </div>
                  </div>
                ) : null}
                {cnpjInfo && cnpjInfo.disponivel === false ? (
                  <FormGroup>
                    <Label style={labelStyle}>Razão social * (a Receita não respondeu; digite)</Label>
                    <Input value={razaoSocial} maxLength={150} onChange={(e) => setRazaoSocial(e.target.value)} aria-label="Razão social" />
                  </FormGroup>
                ) : null}

                <div style={tituloSecao}>Responsável</div>
                <CustomInputGroupText name="nomeResponsavel" placeholder="Nome do responsável *" type="text" addonType="prepend" icon="tim-icons icon-single-02" />
                <Row>
                  <Col md="6">
                    <CustomInputGroupText name="email" placeholder="E-mail *" type="email" addonType="prepend" icon="tim-icons icon-email-85" />
                  </Col>
                  <Col md="6">
                    <CustomInputGroupText name="telefone" placeholder="Telefone ou WhatsApp (com DDD) *" type="tel" addonType="prepend" icon="tim-icons icon-mobile" />
                  </Col>
                </Row>

                <div style={tituloSecao}>Cobertura *</div>
                <p style={{ ...textoSuave, fontSize: 12 }}>Onde você atende: cada rota é uma origem e um destino, por UF inteira ou por cidade. É isso que faz a sua transportadora aparecer para o embarcador.</p>
                <CoberturaEditor value={cobertura} onChange={(l) => { setCobertura(l); setErroCobertura(null); }} carregarEstados={listarEstadosRede} carregarCidades={buscarCidadesRede} erro={erroCobertura} disabled={submitting} />

                <div style={tituloSecao}>Tabela de referência (opcional)</div>
                <p style={{ ...textoSuave, fontSize: 12 }}>PDF, XLS, XLSX ou CSV, até {MAX_ARQUIVOS} arquivos de 10 MB. Só é mostrada ao embarcador se você autorizar; não é usada para cálculo nem auditoria.</p>
                <FormGroup>
                  <EscolherArquivos accept={ACCEPT} multiple onSelecionar={escolherArquivos} ariaLabel="Arquivos da tabela de referência" disabled={submitting} />
                </FormGroup>
                {arquivos.length ? (
                  <ul style={{ ...textoSuave, fontSize: 13, paddingLeft: "1.1rem" }} data-testid="rede-arquivos">
                    {arquivos.map((f, i) => (
                      <li key={`${f.name}-${i}`}>
                        {f.name} <span style={{ opacity: 0.7 }}>({fmtTamanho(f.size)})</span>{" "}
                        <button type="button" className="btn btn-link btn-sm" style={{ color: "#fd5d93", padding: 0 }} onClick={() => removerArquivo(i)}>
                          remover
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {erroArquivos ? <div style={erroStyle} role="alert">{erroArquivos}</div> : null}
                <FormGroup check>
                  <Label check style={{ ...textoSuave, cursor: "pointer" }}>
                    <Input type="checkbox" checked={visivel} onChange={(e) => setVisivel(e.target.checked)} disabled={submitting || !arquivos.length} /> Visível para embarcadores
                    <span className="form-check-sign" />
                  </Label>
                </FormGroup>

                <div style={tituloSecao}>API de cotação (opcional)</div>
                <ApiCotacaoForm value={api} onChange={(v) => { setApi(v); setErrosApi({}); }} erros={errosApi} disabled={submitting} labelStyle={labelStyle} textoStyle={textoSuave} />

                {captcha ? (
                  <>
                  <div style={tituloSecao}>Confirmação</div>
                  <p style={{ ...textoSuave, fontSize: 12 }}>Para confirmar que você não é um robô, responda a conta abaixo.</p>
                  <div style={caixaStyle}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, color: "#333" }}>
                      <span style={{ fontSize: 14 }}>
                        Quanto é <strong style={{ fontFamily: "monospace", fontSize: 16, color: "#0c3c60" }} data-testid="rede-captcha">{String(captcha.question || "").replace(/\s*=\s*\?\s*$/, "").replace(/\s+/g, " ")}</strong>?
                      </span>
                      <button type="button" onClick={refreshCaptcha} style={{ background: "none", border: "none", color: "#0c3c60", cursor: "pointer", fontSize: 12, textDecoration: "underline" }}>
                        Trocar a conta
                      </button>
                    </div>
                    <CustomInputGroupText name="captchaAnswer" placeholder="Resultado da conta" type="text" addonType="prepend" icon="tim-icons icon-puzzle-10" />
                  </div>
                  </>
                ) : null}

                {erroGeral ? (
                  <div style={{ ...erroStyle, marginTop: "0.75rem" }} role="alert" data-testid="rede-erro">
                    {erroGeral.texto}
                    {erroGeral.code === "ja_tem_acesso" ? (
                      <>
                        {" "}
                        <Link to="/auth/login" style={{ color: "#0c3c60", textDecoration: "underline" }}>
                          Ir para o login
                        </Link>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </CardBody>
              <CardFooter>
                <p style={{ ...textoSuave, fontSize: 12, margin: "1.5rem 0 1rem" }}>
                  Ao enviar, você concorda que a cobertura declarada seja usada no Sourcing, no Leilão e no BID da Everlog, e que a tabela de referência só fique visível com a sua autorização. Entrar na Rede não garante frete.
                </p>
                <Button block color="primary" size="lg" type="submit" disabled={submitting}>
                  {submitting ? "Enviando…" : "Pedir entrada na Rede"}
                </Button>
                <div style={{ textAlign: "center", marginTop: 12 }}>
                  <Link to="/auth/login" style={{ color: "#0c3c60", fontSize: 13 }}>
                    Já tenho acesso ao portal
                  </Link>
                </div>
              </CardFooter>
            </Card>
          </Form>
        </Col>
      </Container>
    </div>
  );
}
